import React, { useEffect, useRef, useState } from 'react';

import { Box, Typography } from '@mui/material';
import Checkbox from 'components/V2/Checkbox';
import PropTypes from 'prop-types';

import styles from './channelList.module.css';

// 30 covers the visible area of the drawer's list (~13 rows × 2 columns ≈ 26
// channels at 44px/row) with a small buffer, so nothing blank shows before the
// user scrolls. Same mechanism as
// views/V2/Medallion/Workflow/components/CountryInput/components/Countires.
const BATCH_SIZE = 30;

/**
 * The channel catalogue as a 2-column, row-major grid — CSS grid's default
 * auto-flow lays `channels` out left-to-right, top-to-bottom in array order,
 * so the alphabetical order already maintained in `STR_BOOKING_CHANNELS` falls
 * out for free without any manual column splitting.
 *
 * Renders incrementally rather than all 100+ rows at once: `visibleCount` grows
 * by `BATCH_SIZE` whenever the sentinel div at the end of the rendered rows
 * scrolls into view. `Select all` / `Clear all` and the selection counts in
 * `ChannelSelectionInfo` are untouched by this — they already operate on the
 * full `channels` array the caller passes in, not on how much of it is mounted.
 */
function ChannelList({ channels, isSelected, onToggle, disabled = false }) {
    const [visibleCount, setVisibleCount] = useState(BATCH_SIZE);
    const sentinelRef = useRef(null);

    // A new search result (or a shorter one) must not leave `visibleCount`
    // stuck above the new list's length, and reopening a fresh list shouldn't
    // start mid-scroll.
    useEffect(() => {
        setVisibleCount(BATCH_SIZE);
    }, [channels]);

    // Keyed on `channels.length`, not `channels` itself (unlike the reset effect
    // above) — the observer only needs recreating when the count it clamps
    // against actually changes, not on every new array reference the caller's
    // useMemo hands back.
    useEffect(() => {
        const node = sentinelRef.current;
        if (!node) return undefined;

        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                setVisibleCount((count) => Math.min(count + BATCH_SIZE, channels.length));
            }
        });
        observer.observe(node);

        return () => observer.disconnect();
    }, [channels.length]);

    const visibleChannels = channels.slice(0, visibleCount);

    return (
        <Box className={styles.grid}>
            {visibleChannels.map(({ value, label }) => (
                <Box
                    key={value}
                    className={styles.row}
                    onClick={() => !disabled && onToggle(value)}
                >
                    {/* Stops the row's own onClick from firing a second toggle
                        when the checkbox itself is the click target. */}
                    <Box onClick={(event) => event.stopPropagation()}>
                        <Checkbox
                            checked={isSelected(value)}
                            onChange={() => onToggle(value)}
                            disabled={disabled}
                        />
                    </Box>
                    <Typography className={styles.label}>{label}</Typography>
                </Box>
            ))}
            {visibleCount < channels.length && (
                <div ref={sentinelRef} className={styles.sentinel} />
            )}
        </Box>
    );
}

ChannelList.propTypes = {
    channels: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
        })
    ).isRequired,
    isSelected: PropTypes.func.isRequired,
    onToggle: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
};

export default ChannelList;
